import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Box, Card, CardContent, Tab, Tabs, Tooltip } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import BugReportIcon from "@mui/icons-material/BugReport";

import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import useStyles from "./styles";
import { AuditTrailTab } from "./AuditTrailTab";
import { SystemLogTab } from "./SystemLogTab";
import { AuditFilters, useAuditStats, useSystemLogStats } from "../../api/audit";

/**
 * Journaux — the operator's window onto both log stores.
 *
 * ONE SCREEN, TWO TABLES
 * ----------------------
 * `audit_logs` (who did what) and `system_logs` (what broke) stay separate
 * server-side: different retention, different immutability, different legal
 * weight. But they are read together, because the first question of any
 * incident is "what happened at 14:02" and the answer is usually one row from
 * each. Splitting them across two menu entries would mean correlating
 * timestamps by hand, which is exactly the hour everyone wastes.
 *
 * The KPI strip is clickable: a non-zero "échecs de connexion" is only useful
 * if it is one click from the rows behind it.
 */
type Props = {};

export const Audit: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [tab, setTab] = useState(0);
  /** Filter pushed into the trail tab when a KPI card is clicked. */
  const [seedFilters, setSeedFilters] = useState<AuditFilters | undefined>();
  /** Remounts the tab so a new seed filter is actually adopted. */
  const [seedKey, setSeedKey] = useState(0);

  const { stats } = useAuditStats();
  const { stats: sysStats } = useSystemLogStats();

  const applySeed = (filters: AuditFilters) => {
    setSeedFilters(filters);
    setSeedKey((k) => k + 1);
    setTab(0);
  };

  const kpis = [
    {
      label: t("audit.kpiTotal", "Entrées totales"),
      value: stats?.total ?? 0,
      color: "text.primary",
      onClick: () => applySeed({}),
      hint: t("audit.kpiTotalHint", "Toute la piste d'audit conservée"),
    },
    {
      label: t("audit.kpi24h", "Dernières 24 h"),
      value: stats?.last_24h ?? 0,
      color: "primary.main",
      onClick: () =>
        applySeed({ date_from: new Date().toISOString().slice(0, 10) }),
      hint: t("audit.kpi24hHint", "Activité du jour"),
    },
    {
      label: t("audit.kpiCritical", "Critiques (7 j)"),
      value: stats?.critical_7d ?? 0,
      color: (stats?.critical_7d ?? 0) > 0 ? "error.main" : "text.primary",
      onClick: () => applySeed({ severity: "critical" }),
      hint: t(
        "audit.kpiCriticalHint",
        "Suppressions et changements de droits sensibles"
      ),
    },
    {
      label: t("audit.kpiFailedLogins", "Échecs connexion (24 h)"),
      value: stats?.failed_logins_24h ?? 0,
      color: (stats?.failed_logins_24h ?? 0) > 10 ? "warning.main" : "text.primary",
      onClick: () => applySeed({ action: "auth.login_failed" }),
      hint: t(
        "audit.kpiFailedLoginsHint",
        "Un pic peut signaler une tentative d'intrusion"
      ),
    },
    {
      label: t("audit.kpiErrors", "Erreurs système (24 h)"),
      value: sysStats?.errors_24h ?? 0,
      color: (sysStats?.errors_24h ?? 0) > 0 ? "error.main" : "text.primary",
      onClick: () => setTab(1),
      hint: t("audit.kpiErrorsHint", "Exceptions techniques enregistrées"),
    },
  ];

  // Sparkline scale. Guarded against an all-zero series, which would divide
  // by zero and render NaN% heights.
  const timeline = stats?.timeline ?? [];
  const peak = Math.max(1, ...timeline.map((d) => Number(d.total) || 0));

  return (
    <Sidebar
      styles={{
        sidebarRightRoot: classes.auditRoot,
        sidebarRoot: classes.contentMain,
      }}
    >
      <div className={classes.containerHeader}>
        <div className={classes.divIconArrow}>
          <HistoryIcon sx={{ fontSize: 32, color: "primary.main" }} />
          <CustomTypography
            variant="h2"
            text={t("audit.title", "Journaux")}
            isUppercase
          />
        </div>
      </div>

      {/* ── KPI strip ──────────────────────────────────────────────────── */}
      <div className={classes.statsRow}>
        {kpis.map((kpi) => (
          <Tooltip key={kpi.label} title={kpi.hint as string} arrow>
            <Card
              className={classes.statCard}
              onClick={kpi.onClick}
              sx={{ cursor: "pointer" }}
            >
              <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                <Box className={classes.statValue} sx={{ color: kpi.color }}>
                  {Number(kpi.value).toLocaleString("fr-FR")}
                </Box>
                <Box className={classes.statLabel}>{kpi.label}</Box>
              </CardContent>
            </Card>
          </Tooltip>
        ))}
      </div>

      {/* ── 30-day activity shape ──────────────────────────────────────
          Not a chart library: this conveys shape, and shape (a quiet week
          then a spike) is the only thing a 30-day count needs to convey. */}
      {timeline.length > 1 && (
        <Card className={classes.statCard} sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
            <Box className={classes.statLabel}>
              {t("audit.activity30d", "Activité sur 30 jours")}
            </Box>
            <div className={classes.sparkline}>
              {timeline.map((day) => (
                <Tooltip
                  key={day.day}
                  title={`${day.day} — ${day.total}`}
                  arrow
                >
                  <div
                    className={classes.sparkBar}
                    style={{
                      height: `${Math.max(
                        4,
                        ((Number(day.total) || 0) / peak) * 100
                      )}%`,
                    }}
                  />
                </Tooltip>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            className={classes.tabsBar}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {/* Audit first: it is the tab with legal weight, and the one that
                answers the question people arrive with. */}
            <Tab
              icon={<FactCheckIcon />}
              iconPosition="start"
              label={t("audit.tabTrail", "Activité")}
            />
            <Tab
              icon={
                <Badge
                  color="error"
                  badgeContent={sysStats?.errors_24h ?? 0}
                  max={99}
                  invisible={!sysStats?.errors_24h}
                >
                  <BugReportIcon />
                </Badge>
              }
              iconPosition="start"
              label={t("audit.tabSystem", "Technique")}
            />
          </Tabs>

          {/* Both tabs stay mounted only when active: each owns a paginated
              query, and keeping the hidden one alive would double the polling
              for a table nobody is looking at. */}
          {tab === 0 && (
            <AuditTrailTab key={seedKey} initialFilters={seedFilters} />
          )}
          {tab === 1 && <SystemLogTab />}
        </CardContent>
      </Card>
    </Sidebar>
  );
};
